import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, getUserData } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  shouldRedirectToCreatorDashboard,
  isBrandDashboardAllowedUserType,
  getDenialMessageFromUserData,
  getCreatorDashboardSignInUrl,
} from "../utils/accountTypeMessages";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log('Brand AuthContext: Initializing...');
    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('AuthContext: Firebase auth timeout, proceeding without auth');
      setLoading(false);
    }, 5000); // 5 second timeout

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      clearTimeout(timeoutId);
      setUser(user);
      
      if (user) {
        try {
          // Get user data from Firestore
          const userDataFromFirestore = await getUserData(user.uid);
          console.log('Brand AuthContext: User data from Firestore:', userDataFromFirestore);
          
          if (userDataFromFirestore && shouldRedirectToCreatorDashboard(userDataFromFirestore.userType)) {
            console.log('Brand AuthContext: User is a creator, redirecting to creator dashboard');
            sessionStorage.setItem('brandSignInNotice', getDenialMessageFromUserData(userDataFromFirestore));
            window.location.href = getCreatorDashboardSignInUrl();
            return;
          }

          if (userDataFromFirestore && !isBrandDashboardAllowedUserType(userDataFromFirestore.userType)) {
            console.log('Brand AuthContext: Account type not allowed for brand dashboard:', userDataFromFirestore.userType);
            sessionStorage.setItem('brandSignInNotice', getDenialMessageFromUserData(userDataFromFirestore));
            await signOut(auth);
            navigate("/signin", { replace: true });
            return;
          }

          console.log('Brand AuthContext: User allowed for brand dashboard');
        } catch (error) {
          console.error('Brand AuthContext: Error fetching user data:', error);
        }
      }
      
      setLoading(false);
      
      // Define which routes are considered public (accessible to unauthenticated users)
      // Use pathname without query params for matching
      const currentPath = location.pathname;
      const publicRoutes = ["/signin", "/onboarding", "/forgot-password"]; 
      // Define routes that logged-in users should be redirected away from
      const authRedirectRoutes = ["/signin"]; 

      if (user) {
        // If user is logged in and tries to access a specific auth page (like signin), redirect to dashboard
        // But allow /onboarding even if logged in (in case they need to complete onboarding)
        if (authRedirectRoutes.includes(currentPath)) {
          navigate("/dashboard", { replace: true });
        }
      } else {
        // If user is not logged in and tries to access a private route, redirect to signin
        // But allow public routes like /signin and /onboarding
        if (!publicRoutes.includes(currentPath)) {
          navigate("/signin", { replace: true });
        }
      }
    }, (error) => {
      // Handle auth errors
      console.error('Brand AuthContext: Firebase auth error:', error);
      clearTimeout(timeoutId);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [navigate, location.pathname]);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};